// Camera relay (Raspberry Pi NVR behind cameras.kancraftmfg.com): URLs, auth
// headers, the camera list with friendly names, and the pure clip/time helpers
// the cameras pages share. Nothing here talks to jobq-api.

export const CAMERA_BASE_URL = "https://cameras.kancraftmfg.com";
export const CF_ACCESS_CLIENT_ID = "f6aa5936667f527290786c8d7b896774.access";
export const CF_ACCESS_CLIENT_SECRET = "53e231d91de00cb864cfe49535c6914434a4712bfd3fccfa4a20e3062d5799d8";

const CF_HEADERS = Object.freeze({
  'CF-Access-Client-Id': CF_ACCESS_CLIENT_ID,
  'CF-Access-Client-Secret': CF_ACCESS_CLIENT_SECRET,
});
// One frozen object, so components can pass it as a prop without re-running effects.
export const getCfHeaders = () => CF_HEADERS;

const T = CF_ACCESS_CLIENT_SECRET;
const enc = encodeURIComponent;

export const snapshotUrl = (station, index, quality = 70) =>
  `${CAMERA_BASE_URL}/snapshot/${enc(station)}?index=${index}&quality=${quality}&token=${T}`;
// MJPEG on purpose for the big live view: single-stream HLS blacks out on
// cold-open through the CF Tunnel (go2rtc keeps ~2 segments). See jobq-ui d6c6dff.
export const streamUrl = (station, index) =>
  `${CAMERA_BASE_URL}/stream/${enc(station)}?index=${index}&token=${T}`;
export const listUrl = (station, index, date) =>
  `${CAMERA_BASE_URL}/recordings/list?station=${enc(station)}&index=${index}&date=${date}&token=${T}`;
export const datesUrl = (station, index) =>
  `${CAMERA_BASE_URL}/recordings/dates?station=${enc(station)}&index=${index}&token=${T}`;
export const fileUrl = (station, index, filename) =>
  `${CAMERA_BASE_URL}/recordings/file?station=${enc(station)}&index=${index}&filename=${enc(filename)}&token=${T}`;
// The clips one camera recorded between two times (a finished task's start / end).
export const rangeUrl = (station, index, start, end) =>
  `${CAMERA_BASE_URL}/recordings/range?station=${enc(station)}&index=${index}&start=${enc(start)}&end=${enc(end)}&token=${T}`;
export const thumbUrl = (station, index, filename) =>
  `${CAMERA_BASE_URL}/recordings/thumb?station=${enc(station)}&index=${index}&filename=${enc(filename)}&token=${T}`;

// go2rtc stream IDs follow the relay's slugify rule (Laser, 2 → laser-2;
// North Outdoor, 0 → north-outdoor-0) and must match /opt/go2rtc/go2rtc.yaml.
// HLS is not used by these pages (see the comment on streamUrl); kept for the
// day go2rtc segment retention makes cold-open HLS reliable.
export const slugify = (station) =>
  String(station).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
export const cameraSlug = (station, index) => `${slugify(station)}-${index}`;
export const hlsUrl = (station, index) =>
  `${CAMERA_BASE_URL}/go2rtc/stream.m3u8?src=${cameraSlug(station, index)}`;

// The relay only knows "station + index". These are the names on the cameras
// themselves (their OSD), so the page and the picture agree.
export const CAMERA_NAMES = {
  Laser: ['Bed', 'Operator', 'Outfeed'],
};

// Camera station → JobQ workstation, for the stations /stations/activity and
// /stations/{station}/timeline report on. Shipping, Metal Room and North
// Outdoor have no JobQ station.
export const JOBQ_STATION = {
  Laser: 'Laser',
  Brake: 'Brake',
  Fabrication: 'Fabrication',
  Blast: 'Blast',
};

// Frame width ÷ height when we know it before the first snapshot arrives
// (North Outdoor is a 1536×432 panorama). Everything else defaults to the
// E1 Pro sub-stream's 896×512. The live tile reports the real ratio on load.
export const DEFAULT_ASPECT = 896 / 512;
const KNOWN_ASPECT = { 'north-outdoor-0': 1536 / 432 };
export const WIDE_ASPECT = 2.4;

export function buildCameraList(config) {
  const cams = [];
  for (const [station, count] of Object.entries(config || {})) {
    const n = Number(count) || 0;
    for (let index = 0; index < n; index++) {
      const slug = cameraSlug(station, index);
      const named = CAMERA_NAMES[station]?.[index];
      // An unnamed extra camera on a multi-camera station still needs telling apart.
      const sub = named || (n > 1 ? `Camera ${index + 1}` : '');
      cams.push({
        key: slug,
        slug,
        station,
        index,
        sub,
        label: sub ? `${station} · ${sub}` : station,
        jobqStation: JOBQ_STATION[station] || null,
        aspect: KNOWN_ASPECT[slug] || DEFAULT_ASPECT,
      });
    }
  }
  return cams;
}

// ── Clip filenames: YYYY-MM-DD_HH-MM-SS.mp4, Pi local time (America/Chicago) ──

const CLIP_RE = /^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})\.mp4$/;
export const CLIP_MINUTES = 2;

/** Minute of the day (fractional, seconds included) a clip starts at, or null. */
export function clipStartMinute(filename) {
  const m = CLIP_RE.exec(filename || '');
  if (!m) return null;
  return Number(m[4]) * 60 + Number(m[5]) + Number(m[6]) / 60;
}

/** The clips that parse, oldest first, as {file, start} (start in minutes). */
export function sortClips(files) {
  return (files || [])
    .map((file) => ({ file, start: clipStartMinute(file) }))
    .filter((c) => c.start !== null)
    .sort((a, b) => a.start - b.start);
}

/** Recorded stretches of the day, [{start, end}] in minutes, gaps over a minute kept. */
export function coverageSegments(clips, gapMinutes = 1) {
  const segs = [];
  for (const c of clips) {
    const end = Math.min(c.start + CLIP_MINUTES, 1440);
    const last = segs[segs.length - 1];
    if (last && c.start - last.end <= gapMinutes) last.end = Math.max(last.end, end);
    else segs.push({ start: c.start, end });
  }
  return segs;
}

/**
 * The clip to play for minute `t`: the one covering it (offset = seconds into
 * the clip), else the next one after a gap (offset 0), else the last one.
 */
export function clipAt(clips, t) {
  if (!clips.length) return null;
  let covering = null;
  for (const c of clips) {
    if (c.start <= t) covering = c;
    else break;
  }
  if (covering && t < covering.start + CLIP_MINUTES + 0.05) {
    return { ...covering, offsetSec: Math.max(0, (t - covering.start) * 60) };
  }
  const next = clips.find((c) => c.start > t);
  if (next) return { ...next, offsetSec: 0 };
  const last = clips[clips.length - 1];
  return { ...last, offsetSec: 0 };
}

/** The clip whose start is closest to minute `t` (for thumbnails). */
export function nearestClip(clips, t) {
  let best = null;
  for (const c of clips) {
    if (!best || Math.abs(c.start - t) < Math.abs(best.start - t)) best = c;
  }
  return best;
}

/** The clip after `file`, or null. */
export function nextClip(clips, file) {
  const i = clips.findIndex((c) => c.file === file);
  return i >= 0 && i + 1 < clips.length ? clips[i + 1] : null;
}

// ── Time formatting (minutes of the day, Central) ──

export function fmtClock(minute, { seconds = false } = {}) {
  const total = Math.max(0, Math.round(minute * 60));
  let h = Math.floor(total / 3600) % 24;
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const mm = String(m).padStart(2, '0');
  return seconds ? `${h}:${mm}:${String(s).padStart(2, '0')} ${ap}` : `${h}:${mm} ${ap}`;
}

export function fmtHour(hour) {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

/** "25 min", "1 h 05 min", "3 d" — for how long a station has been in a state. */
export function fmtDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (s < 60) return 'under 1 min';
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${String(min % 60).padStart(2, '0')} min`;
  return `${Math.floor(h / 24)} d`;
}

const CHICAGO_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

function chicagoParts(date) {
  const p = {};
  for (const { type, value } of CHICAGO_PARTS.formatToParts(date)) p[type] = value;
  return p;
}

/** Today's date in Central time, YYYY-MM-DD (the clip folders are Central days). */
export function todayCentral(now = new Date()) {
  const p = chicagoParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Minute of the Central day for a Date (fractional). */
export function centralMinute(date = new Date()) {
  const p = chicagoParts(date);
  return Number(p.hour) * 60 + Number(p.minute) + Number(p.second) / 60;
}

export function shiftDate(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

export function fmtDateLong(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

/** "10:45" / "10:45:17" (24 h) → minutes, or null. */
export function parseClockParam(value) {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value || '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const s = Number(m[3] || 0);
  if (h > 23 || min > 59 || s > 59) return null;
  return h * 60 + min + s / 60;
}

export function clockParam(minute) {
  const total = Math.max(0, Math.round(minute * 60));
  const h = Math.floor(total / 3600) % 24;
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Fetch helpers (CF Access headers ride on every request) ──

export async function fetchRelayJson(url, { signal } = {}) {
  const res = await fetch(url, { headers: getCfHeaders(), credentials: 'include', signal });
  if (!res.ok) throw new Error(`relay ${res.status}`);
  return res.json();
}

/** Fetch a relay file as a blob: URL (CF Access can't do media range requests). */
export async function fetchRelayBlobUrl(url, { signal } = {}) {
  const res = await fetch(url, { headers: getCfHeaders(), credentials: 'include', signal });
  if (!res.ok) throw new Error(`relay ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** A small LRU of blob: URLs that revokes what it evicts. */
export function createBlobCache(max) {
  const map = new Map();
  return {
    get(key) {
      if (!map.has(key)) return undefined;
      const v = map.get(key);
      map.delete(key);
      map.set(key, v);
      return v;
    },
    has: (key) => map.has(key),
    /** Read without counting as a use (a render reading every row must not reorder the LRU). */
    peek: (key) => map.get(key),
    set(key, url) {
      if (map.has(key)) map.delete(key);
      map.set(key, url);
      while (map.size > max) {
        const [oldKey, oldUrl] = map.entries().next().value;
        map.delete(oldKey);
        try { URL.revokeObjectURL(oldUrl); } catch (e) { /* noop */ }
      }
    },
    clear() {
      for (const url of map.values()) {
        try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
      }
      map.clear();
    },
  };
}
